import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSignal } from '@preact/signals-react';
import { toast } from 'react-toastify';
import { FaPlus, FaEdit, FaBan } from 'react-icons/fa';
import { IoIosRefresh } from 'react-icons/io';
import PageHeader from '../../molecules/PageHeader';
import ATable, { ATableHeader } from '../../organisms/ATable';
import Loading from '../../ions/Loading';
import BadgeStatus from '../../atoms/BadgeStatus';
import { fundingApi, Platform } from '../../../services/fundingApi';
import PlatformModalForm from './PlatformModalForm';

const TABLE_HEADERS: ATableHeader[] = [
    { id: 'col-name', title: 'Nome', field: 'name', canSort: true },
    { id: 'col-cetip-lca', title: 'Conta CETIP (LCA)', field: 'cetipAccountLca', canSort: true, width: '150px' },
    { id: 'col-cetip-cdb', title: 'Conta CETIP (CDB)', field: 'cetipAccountCdb', canSort: true, width: '150px' },
    { id: 'col-cetip-di', title: 'Conta CETIP (DI)', field: 'cetipAccountDi', canSort: true, width: '150px' },
    { id: 'col-status', title: 'Status', field: 'active', canSort: true, width: '110px' },
    { id: 'col-actions', title: 'Ações', field: '', canSort: false, width: '120px' },
];

const PlatformListPage: React.FC = () => {
    const navigate = useNavigate();

    const [platforms, setPlatforms] = useState<Platform[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [fieldSorted, setFieldSorted] = useState('name');
    const [orderSorted, setOrderSorted] = useState<'ASC' | 'DESC'>('ASC');

    const showModal = useSignal(false);
    const editingPlatform = useSignal<Platform | null>(null);
    const busyId = useSignal<string | null>(null);

    const loadPlatforms = useCallback(async () => {
        setIsLoading(true);
        // try/finally é obrigatório: listPlatforms pode rejeitar (falha de rede/
        // refresh de token via tokenAwareFetcher), não só resolver com ok:false -
        // sem o finally, a rejeição pulava o setIsLoading(false) e a tela ficava
        // carregando pra sempre, sem toast de erro (bug real).
        try {
            const result = await fundingApi.listPlatforms();
            if (!result.ok) {
                toast.error(result.message);
                if (result.status === 401 || result.status === 403) navigate('/denied');
                return;
            }

            setPlatforms(result.data);
        } catch (error) {
            toast.error(`Não foi possível carregar as plataformas: ${error instanceof Error ? error.message : 'erro inesperado'}.`);
        } finally {
            setIsLoading(false);
        }
    }, [navigate]);

    useEffect(() => {
        loadPlatforms();
    }, [loadPlatforms]);

    const handleSort = (field: string, order: 'ASC' | 'DESC') => {
        setFieldSorted(field);
        setOrderSorted(order);
    };

    // GET /platforms não pagina nem ordena no servidor (lista pequena, ~12
    // itens no seed - ver docs/api-contract.md) - ordenação abaixo é
    // client-side sobre a lista completa já carregada.
    const visiblePlatforms = platforms
        .slice()
        .sort((a, b) => {
            const rawA = (a as unknown as Record<string, unknown>)[fieldSorted];
            const rawB = (b as unknown as Record<string, unknown>)[fieldSorted];
            const aVal = String(rawA ?? '');
            const bVal = String(rawB ?? '');
            if (aVal === bVal) return 0;
            const cmp = aVal < bVal ? -1 : 1;
            return orderSorted === 'ASC' ? cmp : -cmp;
        });

    const openCreate = () => {
        editingPlatform.value = null;
        showModal.value = true;
    };

    const openEdit = (platform: Platform) => {
        if (!platform.active) return;
        editingPlatform.value = platform;
        showModal.value = true;
    };

    const closeModal = () => {
        showModal.value = false;
        editingPlatform.value = null;
    };

    const handleDeactivate = async (platform: Platform) => {
        if (!platform.active) return;
        if (!window.confirm(`Deseja realmente desativar a plataforma "${platform.name}"? Ela deixará de poder ser usada em novas emissões.`)) return;

        busyId.value = platform.id;
        // try/finally: mesmo motivo do loadPlatforms acima - sem ele, uma
        // rejeição (rede/token) deixava a linha travada em "busy" pra sempre,
        // sem toast e sem possibilidade de tentar de novo (bug real).
        try {
            const result = await fundingApi.deactivatePlatform(platform.id);
            if (!result.ok) {
                toast.error(result.message);
                return;
            }

            toast.success('Plataforma desativada.');
            // Aguarda o refetch antes de liberar busyId no finally - senão a
            // linha reaparecia clicável com o status antigo (ativa) por uma
            // janela curta antes da lista atualizar (bug real, mesma classe
            // já corrigida nos handlers de registro do IssuanceListPage).
            await loadPlatforms();
        } catch (error) {
            toast.error(`Não foi possível desativar a plataforma: ${error instanceof Error ? error.message : 'erro inesperado'}.`);
        } finally {
            busyId.value = null;
        }
    };

    const renderActions = (platform: Platform) => {
        const busy = busyId.value === platform.id;
        return (
            <div className="flex items-center justify-center gap-2">
                <button
                    onClick={() => openEdit(platform)}
                    disabled={busy || !platform.active}
                    className="text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={platform.active ? 'Editar' : 'Plataforma inativa - não pode ser editada'}
                >
                    <FaEdit />
                </button>
                <button
                    onClick={() => handleDeactivate(platform)}
                    disabled={busy || !platform.active}
                    className="text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed"
                    title={platform.active ? 'Desativar' : 'Já está inativa'}
                >
                    <FaBan />
                </button>
            </div>
        );
    };

    const renderRows = () =>
        visiblePlatforms.map((row, idx) => (
            <tr
                key={row.id}
                className={`${idx % 2 === 0 ? 'bg-slate-50 dark:bg-slate-800' : 'dark:bg-slate-900'} hover:bg-slate-200 dark:hover:bg-slate-700 border-b border-gray-200 dark:border-gray-700`}
            >
                <td className="py-2 px-2 text-sm font-semibold">{row.name}</td>
                <td className="py-2 px-2 text-sm">{row.cetipAccountLca || <span className="text-gray-400">—</span>}</td>
                <td className="py-2 px-2 text-sm">{row.cetipAccountCdb || <span className="text-gray-400">—</span>}</td>
                <td className="py-2 px-2 text-sm">{row.cetipAccountDi || <span className="text-gray-400">—</span>}</td>
                <td className="py-2 px-2 text-center">
                    <BadgeStatus color={row.active ? 'green' : 'gray'} text={row.active ? 'Ativo' : 'Inativo'} />
                </td>
                <td className="py-2 px-2 text-center">{renderActions(row)}</td>
            </tr>
        ));

    return (
        <div className="px-4 bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100">
            <div className="grid grid-cols-1">
                {isLoading && <Loading />}
                <PageHeader
                    title="Plataformas (Funding)"
                    apiVersion=""
                    breadcrumb={[
                        { label: 'Tesouraria', url: '/funding/platform/list' },
                        { label: 'Plataformas', url: '#' },
                    ]}
                >
                    <div className="pt-4 flex justify-end gap-2">
                        <button
                            onClick={() => loadPlatforms()}
                            className="flex items-center gap-1 px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-100 dark:border-slate-600 dark:text-white dark:hover:bg-slate-700"
                            title="Atualizar lista"
                        >
                            <IoIosRefresh /> Atualizar
                        </button>
                        <button
                            onClick={openCreate}
                            className="flex items-center gap-1 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm dark:bg-blue-600 dark:hover:bg-blue-700"
                        >
                            <FaPlus /> Nova Plataforma
                        </button>
                    </div>
                </PageHeader>

                <ATable headers={TABLE_HEADERS} fieldSorted={fieldSorted} orderSorted={orderSorted} onSort={handleSort} autoResize={true}>
                    {visiblePlatforms.length === 0 ? (
                        <tr>
                            <td colSpan={TABLE_HEADERS.length} className="py-8 text-center text-gray-500 dark:text-gray-400 text-lg">
                                Nenhuma plataforma encontrada.
                            </td>
                        </tr>
                    ) : renderRows()}
                </ATable>
            </div>

            <PlatformModalForm
                show={showModal.value}
                platform={editingPlatform.value}
                onClose={closeModal}
                onSuccess={loadPlatforms}
            />
        </div>
    );
};

export default PlatformListPage;
